import React, { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import ReactApexChart from "react-apexcharts";
import { useNavigate } from "react-router-dom";
import { fetchDotSummary } from "./dotComplianceApi";
import type { DotSummary } from "./types";

/**
 * Compliance dashboard card — same visual pattern as CIS/NIST/PCI widgets.
 */
const DoTDashboardCard: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const [summary, setSummary] = useState<DotSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const s = await fetchDotSummary();
        if (mounted) setSummary(s);
      } catch {
        if (mounted) setSummary(null);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const score = Math.round(summary?.score ?? 0);

  const chartOptions = useMemo(
    () => ({
      chart: {
        height: 170,
        type: "radialBar" as const,
        toolbar: { show: false },
        background: "transparent",
      },
      plotOptions: {
        radialBar: {
          startAngle: 0,
          endAngle: 360,
          hollow: {
            margin: 0,
            size: "70%",
            background: isDark ? "rgba(255, 255, 255, 0.05)" : "#fff",
            image: undefined,
            imageOffsetX: 0,
            imageOffsetY: 0,
            position: "front" as const,
            dropShadow: { enabled: false },
          },
          track: {
            background: isDark ? "rgba(255, 255, 255, 0.1)" : "#eee",
            strokeWidth: "100%",
            margin: 0,
            dropShadow: { enabled: false },
          },
          dataLabels: {
            show: true,
            name: {
              show: true,
              fontSize: "16px",
              fontWeight: 400,
              offsetY: 20,
              color: isDark ? "#fff" : "#888",
            },
            value: {
              show: true,
              fontSize: "25px",
              fontWeight: 600,
              color: isDark ? "#fff" : "#2E2E2E",
              offsetY: -20,
              formatter: (val: number) => Math.round(val) + "%",
            },
          },
        },
      },
      fill: {
        type: "gradient",
        gradient: {
          shade: "dark",
          type: "horizontal",
          shadeIntensity: 0.5,
          gradientToColors: ["#B4C3D1"],
          inverseColors: false,
          opacityFrom: 1,
          opacityTo: 1,
          stops: [0, 100],
        },
      },
      stroke: {
        lineCap: "round" as const,
      },
      colors: ["#55718B"],
      labels: ["Compliance%"],
    }),
    [isDark]
  );

  const chartSeries = useMemo(() => [score], [score]);

  return (
    <Box
      onClick={() => navigate("/compliance-dashboard/dot")}
      sx={{
        borderRadius: 2.5,
        p: 2,
        height: "100%",
        width: "100%",
        aspectRatio: "1/1",
        border: `1px solid ${
          isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"
        }`,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        position: "relative",
        cursor: "pointer",
        bgcolor: isDark ? "#191C24" : "#FFFFFF",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 0.5,
          width: "100%",
          gap: 2,
        }}
      >
        <Typography
          noWrap
          sx={{
            fontSize: "13px",
            fontWeight: 500,
            color: isDark ? "#fff" : "inherit",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          DoT / MBSS
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          my: 0.5,
          mt: -5,
        }}
      >
        {loading ? (
          <CircularProgress size={36} />
        ) : (
          <ReactApexChart
            options={chartOptions}
            series={chartSeries}
            type="radialBar"
            height={210}
          />
        )}
      </Box>

      <Box sx={{ mt: -5, display: "flex", flexDirection: "column", gap: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            color: isDark ? "rgba(255, 255, 255, 0.7)" : "#6B7280",
            gap: 0.5,
          }}
        >
          <Typography sx={{ fontSize: "14px" }}>
            Yes:{" "}
            <span
              style={{
                color: isDark ? "#4ADE80" : "#059669",
                fontWeight: 600,
              }}
            >
              {summary?.yes ?? "—"}
            </span>
          </Typography>
          <Typography sx={{ fontSize: "14px" }}>
            No:{" "}
            <span
              style={{
                color: isDark ? "#F87171" : "#DC2626",
                fontWeight: 600,
              }}
            >
              {summary?.no ?? "—"}
            </span>
          </Typography>
          <Typography sx={{ fontSize: "14px" }}>
            Unset:{" "}
            <span
              style={{
                color: isDark ? "rgba(255, 255, 255, 0.9)" : "#374151",
                fontWeight: 600,
              }}
            >
              {summary?.unset ?? "—"}
            </span>
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: "15px",
            fontWeight: 500,
            color: isDark ? "rgba(255, 255, 255, 0.9)" : "#374151",
            textAlign: "left",
            width: "100%",
            pt: 1,
          }}
        >
          Total Controls: {summary?.total ?? "—"}
        </Typography>
      </Box>
    </Box>
  );
};

export default DoTDashboardCard;
